import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if coming from mobile app with token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const mobileToken = urlParams.get('mobile_token');
    const mobileUser = urlParams.get('mobile_user');
    
    if (mobileToken && mobileUser) {
      // Store the token from mobile
      localStorage.setItem('token', mobileToken);
      try {
        const userData = JSON.parse(decodeURIComponent(mobileUser));
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      } catch (e) {
        console.error('Error parsing mobile user data:', e);
      }
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
    } else {
      // Normal flow - check existing session
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authService.login(email, password);
    setUser(response.data.user);
    return response;
  };

  const register = async (userData) => {
    const response = await authService.register(userData);
    setUser(response.data.user);
    return response;
  };

  const logout = () => {
    authService.logout();
    sessionStorage.removeItem('sc_lic_modal_shown');
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  // ── Inactivity logout (30 min) ─────────────────────────────────────────────
  useEffect(() => {
    if (!user) return; // only when logged in

    const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    const STORAGE_KEY = 'sc_last_activity';

    const doLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('sc_pending_check');
      localStorage.removeItem(STORAGE_KEY);
      window.location.href = '/login?reason=inactivity';
    };

    // Check on mount — covers closing browser and coming back later
    const lastActivity = parseInt(localStorage.getItem(STORAGE_KEY) || '0');
    if (lastActivity > 0 && Date.now() - lastActivity > TIMEOUT_MS) {
      doLogout(); return;
    }

    let timer;
    const resetTimer = () => {
      clearTimeout(timer);
      localStorage.setItem(STORAGE_KEY, String(Date.now())); // persist timestamp
      timer = setTimeout(doLogout, TIMEOUT_MS);
    };

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer(); // start timer on login

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
